/** 原始输入屏「数据存储层运维动作」（dual-entry E6，#5009；data-store 规则 23–27）的请求体与解析。
 *
 * 五组动作都是后台任务：POST 受理即 202 + `job_id`，界面轮询 `GET /jobs/{job_id}` 到终态。
 * 清理预演是只读 `GET /store/prune-preview`，直接返回计划；形状照 `someo_keel/db/prune.py::_summary` 原样，
 * 缺键抛 `ContractError`，不补默认值。
 */

import { ContractError } from "../api/errors";

export type StoreAction = "mirror-sync" | "verify" | "ingest" | "sync" | "prune";

export interface StoreActionDef {
  action: StoreAction;
  /** 按钮文字（raw-data.md「危险动作」节的五组按钮）。 */
  title: string;
  path: string;
  hint: string;
}

export const STORE_ACTIONS: readonly StoreActionDef[] = [
  { action: "mirror-sync", title: "立即同步", path: "/data/mirror/sync", hint: "生产库 → 镜像；与 keel-mirror-sync.timer 同一个脚本，记录写 mirror-sync.json" },
  { action: "verify", title: "重新核对", path: "/store/verify/run", hint: "文件 vs 库；记录写 store-verify.json，上方「库文件核对」读同一份" },
  { action: "ingest", title: "立即落库", path: "/store/ingest", hint: "var/ 文件产物 → 库；与 keel-db-ingest.timer 同一条命令" },
  { action: "sync", title: "立即迁入", path: "/store/sync", hint: "镜像 → 库的六张沿用表，回看 N 天；与 keel-db-sync.sh 同一条命令" },
  { action: "prune", title: "清理执行", path: "/store/prune", hint: "单事务删除文件侧已不存在的策略日投影；先预演再执行" },
];

export interface StoreActionInput {
  operator: string;
  reason: string;
  /** 立即同步：可选交易日（缺省由脚本按交易日历判定）。 */
  trade_date?: string;
  /** 立即同步 / 立即迁入：回看天数。 */
  lookback_days?: string;
  /** 立即同步：演练（只统计不写镜像）。 */
  dry_run?: boolean;
  /** 重新核对：同时核对镜像行数。 */
  mirror?: boolean;
  /** 立即落库：不看指纹强制重落。 */
  force?: boolean;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const MAX_LOOKBACK_DAYS = 3650;

/** 校验并组请求体；不合法返回原因（界面置灰并提示），合法返回请求体。布尔一律发真布尔。 */
export function storeActionBody(action: StoreAction, input: StoreActionInput): { body: Record<string, unknown> } | { error: string } {
  const operator = input.operator.trim();
  const reason = input.reason.trim();
  if (!operator || !reason) return { error: "操作人与理由必填（会随动作落盘）" };
  const body: Record<string, unknown> = { operator, reason };
  if (action === "mirror-sync" || action === "sync") {
    const raw = (input.lookback_days ?? "").trim();
    if (raw) {
      if (!/^\d+$/.test(raw) || Number(raw) < 1 || Number(raw) > MAX_LOOKBACK_DAYS) {
        return { error: `回看天数必须是 1…${MAX_LOOKBACK_DAYS} 的整数` };
      }
      body["lookback_days"] = Number(raw);
    }
  }
  if (action === "mirror-sync") {
    const day = (input.trade_date ?? "").trim();
    if (day) {
      if (!DATE_RE.test(day)) return { error: "交易日格式为 YYYY-MM-DD" };
      body["trade_date"] = day;
    }
    body["dry_run"] = input.dry_run === true;
  }
  if (action === "verify") body["mirror"] = input.mirror === true;
  if (action === "ingest") body["force"] = input.force === true;
  return { body };
}

export interface PrunePlanRow {
  strategy_id: string;
  missing_days: string[];
  tables: Record<string, number>;
}

export interface PrunePreview {
  var: string;
  tables: Record<string, number>;
  strategies: PrunePlanRow[];
  /** 计划要删的总行数；0 即无可清理。 */
  total: number;
}

function counts(value: unknown, url: string, where: string): Record<string, number> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new ContractError(`\`${where}\` 应为对象`, url, value);
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(value)) {
    if (typeof v !== "number") throw new ContractError(`\`${where}.${k}\` 应为数字`, url, value);
    out[k] = v;
  }
  return out;
}

/** 解析 `GET /store/prune-preview`。 */
export function parsePrunePreview(body: unknown, url = "/store/prune-preview"): PrunePreview {
  if (typeof body !== "object" || body === null || Array.isArray(body)) throw new ContractError("响应应为对象", url, body);
  const r = body as Record<string, unknown>;
  for (const key of ["var", "apply", "strategies", "tables"]) {
    if (!(key in r)) throw new ContractError(`缺字段 \`${key}\``, url, body);
  }
  if (r["apply"] !== false) throw new ContractError("预演响应的 `apply` 必须是 false", url, body);
  const tables = counts(r["tables"], url, "tables");
  const rawStrategies = r["strategies"];
  if (typeof rawStrategies !== "object" || rawStrategies === null || Array.isArray(rawStrategies)) {
    throw new ContractError("`strategies` 应为对象", url, body);
  }
  const strategies = Object.entries(rawStrategies as Record<string, unknown>).map(([sid, item]) => {
    if (typeof item !== "object" || item === null) throw new ContractError(`\`strategies.${sid}\` 应为对象`, url, body);
    const row = item as Record<string, unknown>;
    const days = row["missing_days"];
    if (!Array.isArray(days) || days.some((d) => typeof d !== "string")) {
      throw new ContractError(`\`strategies.${sid}.missing_days\` 应为字符串数组`, url, body);
    }
    return { strategy_id: sid, missing_days: days as string[], tables: counts(row["tables"], url, `strategies.${sid}.tables`) };
  });
  return {
    var: String(r["var"]),
    tables,
    strategies,
    total: Object.values(tables).reduce((sum, n) => sum + n, 0),
  };
}
